import { Observable, forkJoin, from, map, of, switchMap } from "rxjs";

import { services } from "@progressive-development/pd-spa-helper";
import type { IDatabaseProvider } from "@progressive-development/pd-provider-interfaces";
import type { boden, statistik, wahl } from "@progressive-development/politik-model";

import {
  COL_BODENRICHTWERTE,
  COL_VERZEICHNISSE,
  COL_STRUKTURDATEN,
  COL_STIMMBEZIRKE,
  COL_WAHLRAEUME,
  DOK_BODENRICHTWERTE,
  DOK_INDIKATOREN,
  DB_SHARED,
} from "../../../config/collections.js";

/**
 * Der Gebietsschlüssel der Gesamtstadt.
 *
 * Er steht hier und nicht im Aufrufer: Es ist ein Wert des Datenbestands, kein
 * Bedienzustand — und die einzige Stelle, an der die App weiß, wie die
 * Bezugsgröße heißt.
 */
export const GEBIET_GESAMTSTADT = "gesamtstadt:0";

/** Was Block 6 zum Zeichnen braucht — beide Reihen und die Beschriftungen. */
export interface GebietZahlen {
  /** Gebietsschlüssel, für den geladen wurde. */
  fuer: string;
  indikatoren?: statistik.IndikatorenDokument;
  /** Eine Zeile je Jahr für dieses Gebiet. */
  eigene: statistik.Strukturdaten[];
  /** Dieselben Jahre für die Gesamtstadt. */
  stadt: statistik.Strukturdaten[];
}

/** Was der Boden-Block zum Zeichnen braucht — beide Gebiete und die Codes. */
export interface GebietBoden {
  /** Gebietsschlüssel, für den geladen wurde. */
  fuer: string;
  verzeichnis?: boden.BodenrichtwerteDokument;
  /**
   * ALLE Stichtage dieses Gebiets, nicht nur der jüngste.
   *
   * Die Tabelle zeigt den aktuellen Stand, die Kurve daneben braucht die
   * Reihe. Welcher Stand der aktuelle ist, entscheidet der Stichtag — nicht
   * die Reihenfolge der Antwort.
   */
  eigene: boden.BodenrichtwerteGebiet[];
  /** Dieselben Stichtage für die Gesamtstadt — die Einordnung. */
  stadt: boden.BodenrichtwerteGebiet[];
}

/**
 * Die Strukturdaten eines Bezirks — Block 6.
 *
 * BEIDE Teile liegen in der GETEILTEN Datenbank: Strukturdaten und ihre
 * Beschriftungen sind portalübergreifend. Ein Verkehrs- oder Kulturportal
 * derselben Stadt braucht dieselben Zahlen mit denselben Einheiten.
 *
 * EINE Abfrage für 28 Zeilen: der Bezirk und die Gesamtstadt zusammen, über
 * `in` auf den Gebietsschlüssel. Zwei getrennte Abfragen kosteten dasselbe und
 * kämen zu verschiedenen Zeiten an — der Vergleich braucht aber beide Reihen.
 *
 * OHNE `orderBy`: Das verlangte einen zusammengesetzten Index, und 28 Zeilen
 * ordnet die Anzeige selbst. Wichtiger noch — ein `orderBy` schlösse Dokumente
 * ohne dieses Feld lautlos aus.
 *
 * OHNE `limit`, und das ist hier richtig: Die Bedingung begrenzt das Ergebnis
 * bereits auf zwei Gebiete, also auf eine Zeile je Gebiet und Jahr (derzeit
 * 2 × 14). Eine feste Obergrenze daneben wäre eine stille Kappung, sobald ein
 * Jahrgang hinzukommt — und ein fehlendes Jahr fiele niemandem auf.
 */
export class GebietService {
  private get _shared(): IDatabaseProvider {
    const db = services.databaseNamed(DB_SHARED);
    if (!db) {
      throw new Error(
        "Keine geteilte Datenbank konfiguriert — Strukturdaten und Indikatoren liegen dort " +
          "(databases in config/provider-setup.ts).",
      );
    }
    return db;
  }

  /**
   * Die Bodenrichtwerte eines Gebiets — dasselbe Muster wie die Strukturdaten.
   *
   * EINE Abfrage für beide Gebiete (`in` auf den Gebietsschlüssel), dazu das
   * Verzeichnis mit den Klartexten der Codes. Ohne das Verzeichnis stünden in
   * der Tabelle „B/WA" statt „allgemeines Wohngebiet, baureifes Land".
   *
   * MEHRERE STICHTAGE je Gebiet, seit die Pipeline Jahrgänge liefert: Die
   * Abfrage liefert je Gebiet ein Dokument JE STICHTAG. Beide Reihen kommen
   * vollständig zurück; welcher Stand der aktuelle ist, entscheidet die
   * Anzeige über den Stichtag.
   *
   * Die Gesamtstadt kommt IMMER mit und wird nicht durch ein gewähltes
   * Vergleichsgebiet ersetzt: Der Block steht über der Gebietsauswahl der
   * Strukturdaten — änderte sich oberhalb der Bedienung etwas, wäre das nicht
   * zu verstehen.
   */
  public ladeBodenrichtwerte(gebietId: string): Observable<GebietBoden> {
    return forkJoin({
      verzeichnis: from(
        this._shared.getDocument<boden.BodenrichtwerteDokument>(
          COL_VERZEICHNISSE,
          DOK_BODENRICHTWERTE,
        ),
      ).pipe(map((snap) => (snap.exists ? snap.data : undefined))),
      zeilen: from(
        this._shared.getCollection<boden.BodenrichtwerteGebiet>(COL_BODENRICHTWERTE, {
          constraints: [
            {
              type: "where",
              field: "gebietId",
              operator: "in",
              value: [gebietId, GEBIET_GESAMTSTADT],
            },
          ],
        }),
      ).pipe(map((seite) => seite.documents.map((d) => d.data))),
    }).pipe(
      map(({ verzeichnis, zeilen }) => ({
        fuer: gebietId,
        verzeichnis,
        eigene: zeilen.filter((z) => z.gebietId === gebietId),
        stadt: zeilen.filter((z) => z.gebietId === GEBIET_GESAMTSTADT),
      })),
    );
  }

  public ladeStrukturdaten(gebietId: string): Observable<GebietZahlen> {
    return forkJoin({
      indikatoren: from(
        this._shared.getDocument<statistik.IndikatorenDokument>(COL_VERZEICHNISSE, DOK_INDIKATOREN),
      ).pipe(map((snap) => (snap.exists ? snap.data : undefined))),
      zeilen: from(
        this._shared.getCollection<statistik.Strukturdaten>(COL_STRUKTURDATEN, {
          constraints: [
            {
              type: "where",
              field: "gebietId",
              operator: "in",
              value: [gebietId, GEBIET_GESAMTSTADT],
            },
          ],
        }),
      ).pipe(map((seite) => seite.documents.map((d) => d.data))),
    }).pipe(
      map(({ indikatoren, zeilen }) => ({
        fuer: gebietId,
        indikatoren,
        eigene: zeilen.filter((z) => z.gebietId === gebietId),
        stadt: zeilen.filter((z) => z.gebietId === GEBIET_GESAMTSTADT),
      })),
    );
  }

  /**
   * Die Zahlenreihen EINES Gebiets — für den Vergleich.
   *
   * Ohne die Gesamtstadt, anders als `ladeStrukturdaten`: Der Vergleich TRITT
   * an ihre Stelle, er kommt nicht zu ihr hinzu. Drei Zahlenspalten
   * nebeneinander wären nicht mehr zu lesen.
   *
   * Die Beschriftungen (`indikatoren`) werden nicht erneut geholt — sie hängen
   * am Merkmal, nicht am Gebiet, und liegen längst vor.
   */
  public ladeVergleich(gebietId: string): Observable<VergleichsZeilen> {
    return from(
      this._shared.getCollection<statistik.Strukturdaten>(COL_STRUKTURDATEN, {
        constraints: [{ type: "where", field: "gebietId", operator: "==", value: gebietId }],
      }),
    ).pipe(map((seite) => ({ fuer: gebietId, zeilen: seite.documents.map((d) => d.data) })));
  }

  /**
   * Die Wahllokale eines Stadtteils — über die Stimmbezirke.
   *
   * ZWEI SCHRITTE, weil die Wahl-Gliederung quer zur kommunalen liegt: Ein
   * Wahlraum kennt nur seinen Stimmbezirk, und erst der Stimmbezirk trägt die
   * amtliche Stadtteilnummer. Verknüpft wird über NUMMERN, nie über Namen.
   *
   * Die Briefwahl fällt dabei von selbst heraus: Ihre Stimmbezirke tragen gar
   * keine Stadtteilnummer (503 von 1006) — ein Briefwahlbezirk liegt in keinem
   * Veedel. Wir müssen also nichts wegfiltern, was die Daten nicht ohnehin
   * schon trennen.
   *
   * Das `in` verträgt 30 Werte; der größte Stadtteil hat 18 Stimmbezirke.
   * Sollte je einer darüber liegen, meldet die Abfrage es — geräuschlos
   * abschneiden wäre schlimmer als ein Fehler.
   */
  public ladeWahllokale(stadtteilNr: string): Observable<Wahllokale> {
    return from(
      this._shared.getCollection<wahl.Stimmbezirk>(COL_STIMMBEZIRKE, {
        constraints: [{ type: "where", field: "stadtteilNr", operator: "==", value: stadtteilNr }],
      }),
    ).pipe(
      switchMap((seite) => {
        const nummern = seite.documents.map((d) => d.data.nr).filter(Boolean);
        if (nummern.length === 0) return of({ fuer: stadtteilNr, raeume: [] });
        return from(
          this._shared.getCollection<wahl.Wahlraum>(COL_WAHLRAEUME, {
            constraints: [
              {
                type: "where",
                field: "stimmbezirkNr",
                operator: "in",
                value: nummern.slice(0, 30),
              },
            ],
          }),
        ).pipe(map((wr) => ({ fuer: stadtteilNr, raeume: wr.documents.map((d) => d.data) })));
      }),
    );
  }
}

/** Die Zahlenreihen eines Vergleichsgebiets. */
export interface VergleichsZeilen {
  /** Gebietsschlüssel, für den geladen wurde. */
  fuer: string;
  zeilen: statistik.Strukturdaten[];
}

/** Die Wahlräume eines Stadtteils, roh — zusammengefasst wird in der Anzeige. */
export interface Wahllokale {
  /** Amtliche Stadtteilnummer, für die geladen wurde. */
  fuer: string;
  raeume: wahl.Wahlraum[];
}

export const gebietService = new GebietService();
